import React, { useState, useEffect, useRef, useCallback } from "react";
import jwtDecode from "jwt-decode";
import ChatServices from "../../services/chat.services";
import { getCurrentTimeWithAMPM } from "../../helpers/DateTimeFormatter";
import { CHAT_API_BASE_URL } from "../../config";
import { sliceStringHandler } from "../../helpers/StringSlicer";
import { toast } from "react-toastify";
import { FiSearch, FiSend, FiUser, FiPhone, FiMail } from "react-icons/fi";
import "react-loading-skeleton/dist/skeleton.css";
import Skeleton from "react-loading-skeleton";

const AdminChatWindow = () => {
    const [messages, setMessages] = useState([]);
    const [currentChat, setCurrentChat] = useState(null);
    const [textMessage, setTextMessage] = useState("");
    const [allChats, setAllChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const stompClientRef = useRef(null);
    const isConnecting = useRef(false);
    const containerRef = useRef(null);
    const currentChatRef = useRef(null);

    const token = localStorage.getItem("user");
    const decoded = token ? jwtDecode(token) : null;
    const UserId = decoded?.sub;
    const userName = decoded?.name;
    const imageUrl = decoded?.imageUrl || "/images/profile-picture.webp";

    // Keep the ref in sync with state
    useEffect(() => {
        currentChatRef.current = currentChat;
    }, [currentChat]);

    const connect = useCallback(() => {
        if (!UserId || isConnecting.current) return;
        if (stompClientRef.current && stompClientRef.current.connected) return;

        isConnecting.current = true;
        const Stomp = require("stompjs");
        var SockJS = require("sockjs-client");
        const socket = new SockJS(CHAT_API_BASE_URL);
        const client = Stomp.over(socket);

        client.debug = null;

        const onConnectedLocal = () => {
            isConnecting.current = false;
            stompClientRef.current = client;
            client.subscribe(
                "/user/" + UserId + "/queue/messages",
                onMessageReceived
            );
        };

        const onErrorLocal = (err) => {
            isConnecting.current = false;
            console.error("STOMP connection error:", err);
            // Retry after 5 seconds
            setTimeout(connect, 5000);
        };

        client.connect({}, onConnectedLocal, onErrorLocal);
    }, [UserId]);

    const onMessageReceived = (msg) => {
        const message = JSON.parse(msg.body);
        const activeChat = currentChatRef.current;

        // If the message is for the current conversation, add it to the list
        setMessages((prev) => {
            const alreadyExists = prev.some((m) => m.id === message.id);
            if (alreadyExists) return prev;

            // Check if it belongs to current chat
            if (activeChat && String(message.senderId) === String(activeChat.userSummary.id)) {
                return [...prev, message];
            }
            return prev;
        });

        // Always refresh chat list to show recent messages/counts
        loadAllChats();

        if (activeChat && String(message.senderId) === String(activeChat.userSummary.id)) {
            setTimeout(scrollToBottom, 300);
        }
    };

    const scrollToBottom = () => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    };

    const loadAllChats = async () => {
        try {
            const res = await ChatServices.getAllChats();
            if (res.data) {
                // Filter to only show support chats (type === "support" or productId === 0)
                // This prevents duplicate entries when a user has both support and product chats
                const supportChats = res.data.filter(chat =>
                    chat.type === "support" || chat.productId === 0 || chat.productId === null
                );

                // Deduplicate by user ID - keep only the most recent conversation per user
                const uniqueByUser = {};
                supportChats.forEach(chat => {
                    const userId = chat.userSummary?.id;
                    if (!userId) return;
                    if (!uniqueByUser[userId]) {
                        uniqueByUser[userId] = chat;
                    } else {
                        // Keep the one with the more recent message
                        const existingTime = uniqueByUser[userId].recentMessage?.timestamp;
                        const currentTime = chat.recentMessage?.timestamp;
                        if (currentTime && (!existingTime || new Date(currentTime) > new Date(existingTime))) {
                            uniqueByUser[userId] = chat;
                        }
                    }
                });

                setAllChats(Object.values(uniqueByUser));
            }
        } catch (error) {
            console.error("Failed to load admin chats:", error);
        } finally {
            setLoading(false);
        }
    };

    const openChat = async (chat) => {
        setCurrentChat(chat);
        setMessages([]);
        try {
            const res = await ChatServices.getMessagesSupport(UserId, chat.userSummary.id);
            if (res.data) {
                setMessages(res.data);
                setTimeout(scrollToBottom, 300);
            }
        } catch (error) {
            console.error("Failed to load messages:", error);
            toast.error("Failed to load conversation history");
        }
    };

    const sendMessage = () => {
        if (!textMessage.trim() || !currentChat) return;

        if (stompClientRef.current && stompClientRef.current.connected) {
            const chatMessage = {
                senderId: Number(UserId),
                senderName: "ADMIN",
                recipientId: currentChat.userSummary.id,
                recipientName: currentChat.userSummary.name,
                content: textMessage,
                timestamp: new Date(),
                productId: 0, // General support
                productTitle: "General Support"
            };

            stompClientRef.current.send("/app/chat", {}, JSON.stringify(chatMessage));
            setMessages((prev) => [...prev, chatMessage]);
            setTextMessage("");
            setTimeout(scrollToBottom, 300);

            // Refresh list to show latest message in sidebar
            loadAllChats();
        } else {
            toast.warning("Chat disconnected. Reconnecting...");
            connect();
        }
    };

    useEffect(() => {
        loadAllChats();
        connect();
        return () => {
            if (stompClientRef.current) {
                stompClientRef.current.disconnect();
            }
        };
    }, [connect]);

    const filteredChats = allChats.filter(chat =>
        chat.userSummary.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chat.userSummary.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="admin-chat-layout">
            {/* Sidebar */}
            <div className="admin-chat-sidebar">
                <div className="sidebar-header">
                    <h3>Recent Conversations</h3>
                    <div className="search-box">
                        <FiSearch />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="sidebar-list">
                    {loading ? (
                        [...Array(5)].map((_, i) => (
                            <div key={i} className="skeleton-chat-item">
                                <Skeleton circle height={40} width={40} />
                                <div style={{ flex: 1, marginLeft: 10 }}>
                                    <Skeleton width="60%" />
                                    <Skeleton width="40%" />
                                </div>
                            </div>
                        ))
                    ) : filteredChats.length > 0 ? (
                        filteredChats.map(chat => (
                            <div
                                key={`${chat.userSummary.id}_${chat.productId || 0}`}
                                className={`chat-item ${currentChat?.userSummary.id === chat.userSummary.id ? 'active' : ''}`}
                                onClick={() => openChat(chat)}
                            >
                                <div className="chat-avatar">
                                    <img
                                        src={chat.userSummary.profilePicture || "/images/profile-picture.webp"}
                                        alt={chat.userSummary.name}
                                        onError={(e) => e.target.src = "/images/profile-picture.webp"}
                                    />
                                    {chat.messageCount > 0 && <span className="unread-badge">{chat.messageCount}</span>}
                                </div>
                                <div className="chat-info">
                                    <div className="chat-name-row">
                                        <h4>{chat.userSummary.name}</h4>
                                        <span className="chat-time">
                                            {chat.recentMessage ? getCurrentTimeWithAMPM(chat.recentMessage.timestamp) : ''}
                                        </span>
                                    </div>
                                    <p className="chat-last-msg">
                                        {chat.recentMessage ? sliceStringHandler(chat.recentMessage.content) : 'No messages yet'}
                                    </p>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="empty-chats">
                            <p>No conversations found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Chat Area */}
            <div className="admin-chat-main">
                {currentChat ? (
                    <>
                        <div className="chat-header">
                            <div className="header-user">
                                <img
                                    src={currentChat.userSummary.profilePicture || "/images/profile-picture.webp"}
                                    alt={currentChat.userSummary.name}
                                    className="user-avatar"
                                />
                                <div className="user-meta">
                                    <h4>{currentChat.userSummary.name}</h4>
                                    <span className="user-status">Online</span>
                                </div>
                            </div>
                            <div className="header-actions">
                                <a href={`tel:${currentChat.userSummary.mobile}`} title="Call User"><FiPhone /></a>
                                <a href={`mailto:${currentChat.userSummary.email}`} title="Email User"><FiMail /></a>
                            </div>
                        </div>

                        <div className="chat-messages" ref={containerRef}>
                            {messages.map((msg, idx) => {
                                const isMe = String(msg.senderId) === String(UserId);
                                return (
                                    <div key={idx} className={`message-row ${isMe ? 'me' : 'them'}`}>
                                        {!isMe && (
                                            <img
                                                src={currentChat.userSummary.profilePicture || "/images/profile-picture.webp"}
                                                className="msg-avatar"
                                                alt="avatar"
                                            />
                                        )}
                                        <div className="message-content">
                                            <div className="message-bubble">
                                                {msg.content}
                                            </div>
                                            <span className="message-time">
                                                {getCurrentTimeWithAMPM(msg.timestamp)}
                                            </span>
                                        </div>
                                        {isMe && (
                                            <img
                                                src={imageUrl}
                                                className="msg-avatar"
                                                alt="my avatar"
                                                onError={(e) => e.target.src = "/images/profile-picture.webp"}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="chat-input-area">
                            <input
                                type="text"
                                placeholder="Type a message..."
                                value={textMessage}
                                onChange={(e) => setTextMessage(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                            />
                            <button className="send-btn" onClick={sendMessage} disabled={!textMessage.trim()}>
                                <FiSend />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="chat-placeholder">
                        <div className="placeholder-content">
                            <FiUser size={60} />
                            <h3>Select a conversation</h3>
                            <p>Choose a user from the sidebar to start messaging</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminChatWindow;
